import React, { KeyboardEventHandler, useState } from 'react';
import cx from 'classnames';
import styles from '../sass/components/TextInput.module.scss';
import Alert from './Alert';

export interface TextInputProps {
  title?: React.ReactNode;
  disabled?: boolean;
  errorMessage?: string;
  success?: boolean;
  id?: string;
  value?: string;
  onChange?: (event: React.FormEvent<HTMLInputElement>) => void;
  onEnter?: () => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  rightIcon?: React.ReactNode;
  rightButton?: React.ReactNode;
  type?: string;
  className?: string;
  hint?: string;
  autoComplete?: string;
  name?: string
  max?: number
  min?: number
  maxlength?: number
  minlength?: number
  onKeyDown?: KeyboardEventHandler
  isRequired?: boolean
  /**
   * Marks the field required for assistive tech (aria-required) without
   * the browser's own validation bubble; the form reports it in its words.
   */
  required?: boolean
  /** The id of an element that labels the box (when `title` does not). */
  labelledBy?: string
  /** An accessible name, for a box with no visible label of its own. */
  ariaLabel?: string
  /** Extra ids for aria-describedby (the error and hint are added here). */
  describedBy?: string
  readOnly?: boolean
  /** Popup the box opens (a read-only box that opens a picker). */
  ariaHasPopup?: 'dialog' | 'listbox'
  ariaExpanded?: boolean
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
}

let autoIdCounter = 0;

/**
 * A stable id for a box whose caller gave none (React 17 has no useId): the
 * label's `for`, the error and the hint all hang off it, so every box with a
 * `title` is programmatically labelled (QA 2026-09-25 #30).
 */
function useAutoId(given?: string): string {
  const [auto] = useState(() => {
    autoIdCounter += 1;
    return `text-input-${autoIdCounter}`;
  });
  return given || auto;
}

const TextInput = React.forwardRef<HTMLInputElement | null, TextInputProps>((
  props, ref,
) => {
  const {
    title,
    disabled = false,
    errorMessage,
    success,
    id,
    value,
    onChange,
    onBlur,
    placeholder,
    rightIcon,
    rightButton,
    type = 'text',
    onEnter,
    onClick,
    className,
    hint,
    autoComplete,
    name,
    max,
    min,
    maxlength,
    minlength,
    onKeyDown,
    isRequired,
    required,
    labelledBy,
    ariaLabel,
    describedBy,
    readOnly,
    ariaHasPopup,
    ariaExpanded,
    inputMode,
  } = props;

  const inputId = useAutoId(id);
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const showHint = !!hint && !errorMessage;
  // The error, the hint and whatever the caller adds, in reading order.
  const describedByIds = [
    errorMessage ? errorId : '',
    showHint ? hintId : '',
    describedBy || '',
  ].filter(Boolean).join(' ') || undefined;

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && onEnter) {
      event.preventDefault();
      onEnter();
    }
  };
  return (
    <div className={cx(styles.textInputWrapper, className)}>
      {title && (
        <label
          htmlFor={inputId}
          className={cx({
            [styles.disabled]: disabled,
          })}
        >
          {title}
        </label>
      )}
      <div className={cx(styles.inputWrapper,
        { [styles.inputHasRightIcon]: !!rightIcon })}
      >
        <input
          ref={ref}
          onChange={onChange}
          onKeyPress={handleKeyPress}
          onClick={onClick}
          onBlur={onBlur}
          id={inputId}
          type={type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          className={cx({
            [styles.inputDisabled]: disabled,
            [styles.error]: errorMessage,
            [styles.success]: success,
            [styles.hasRightButton]: !!rightButton,
          })}
          name={name}
          maxLength={maxlength}
          max={max}
          min={min}
          minLength={minlength}
          onKeyDown={onKeyDown}
          required={isRequired}
          readOnly={readOnly}
          inputMode={inputMode}
          aria-required={required || undefined}
          aria-invalid={errorMessage ? true : undefined}
          aria-describedby={describedByIds}
          aria-labelledby={labelledBy}
          aria-label={ariaLabel}
          // A read-only box that opens a picker is a combobox (ARIA 1.2):
          // the role that carries aria-haspopup and aria-expanded.
          role={ariaHasPopup ? 'combobox' : undefined}
          aria-haspopup={ariaHasPopup}
          aria-expanded={ariaHasPopup ? !!ariaExpanded : undefined}
        />
        {rightButton && rightButton}
        {rightIcon && (
          <div className={styles.rightIcon}>
            {rightIcon}
          </div>
        )}
      </div>
      {showHint && (
        <div className={styles.hintWrapper}>
          <span className="smallLabel" id={hintId}>{hint}</span>
        </div>
      )}
      {/* A polite live region that is always there, so an error that
          appears (on blur, on submit) is read out, and tied to the box by
          aria-describedby. Empty and unstyled when there is no error. */}
      <div id={errorId} aria-live="polite">
        <Alert message={errorMessage} type="error" />
      </div>
    </div>
  );
});

export default TextInput;
